// TemplateTreatmentsPage.js

import { useState, useEffect, useRef, useContext } from "react";
import { useFetch } from "use-http";
import { UserContext } from "../App";
import { Box, Button, Spinner, Text, Image, Select, ResponsiveContext, Layer, Header, TextInput} from "grommet";
import { ThemeContext } from 'grommet/contexts';
import { getDBURL, updateStateHandler, calculateHeight, correctTimeZone, generateSyncId} from "../components/Utils/Utils";
import { FormUp, FormDown } from "grommet-icons";
import HomePageNav from "../components/Base components/HomePageNav";
import TreatmentTable from "../components/Treatment/TreatmentTable";
import WarningPopupWindow from "../components/Base components/WarningPopupWindow";
import Notification from "../components/Base components/Notification";
import ConfirmPopupWindow from "../components/Base components/ConfirmPopupWindow";
import { useErrorLogger } from '../components/Base components/Logger';



// ----------------------------  pop-up window for change priority  ----------------------------------------

const ChangePriorityOfTemplatesPopup = (props)=>{
    const { post } = useFetch(getDBURL());
    const { onClose, templatesList, baseTreatment, setHasEdit, updateTreatmentValuesList } = props;
    const currentUser = useContext(UserContext);
    const theme = useContext(ThemeContext);
    const { DBlogError } = useErrorLogger();
    const [localList, setLocalList] = useState(templatesList);
    const prevTreatmentsList = useRef(templatesList);

    const [error, setError]= useState('');

    console.log('In ChangePriorityOfTemplatesPopup props obj:', props);

    const onMoveUp = (id) => {
        setLocalList((prevValues) => {
            const index = prevValues.findIndex(item => item.TreatmentID === id);
            if (index === -1 || index === 0) return prevValues; // Already at the top or not found

            const newValues = prevValues.map((item, idx) => {
                if (idx === index) {
                    return { ...item, Priority: prevValues[index - 1].Priority };
                } else if (idx === index - 1) {
                    return { ...item, Priority: prevValues[index].Priority };
                } else {
                    return item;
                }
            });

            return newValues.sort((a, b) => a.Priority - b.Priority);
        });
    };
    
    const onMoveDown = (id) => {
        setLocalList((prevValues) => {
            const index = prevValues.findIndex(item => item.TreatmentID === id);
            if (index === -1 || index === prevValues.length - 1) return prevValues; // Already at the bottom or not found

            const newValues = prevValues.map((item, idx) => {
                if (idx === index) {
                    return { ...item, Priority: prevValues[index + 1].Priority };
                } else if (idx === index + 1) {
                    return { ...item, Priority: prevValues[index].Priority };
                } else {
                    return item;
                }
            });

            return newValues.sort((a, b) => a.Priority - b.Priority);
        });
    };
    
    const handleSave = async () => {
        try {
            const isValueChanged = JSON.stringify(localList) !== JSON.stringify(prevTreatmentsList.current);
    
            if (isValueChanged) {
                for (const el of localList) {
                    const updatedItem = {
                        ...el,
                        IsDefault: 0,
                        Priority: el.Priority,
                        UpdatedAt: correctTimeZone(new Date()),
                        UpdatedBy: currentUser.name,
                        LastSyncDate: correctTimeZone(new Date()),
                    };
    
                    const response = await post("/updateDefaultTreatment", updatedItem);
    
                    if (!response.success) {
                        throw new Error(`Failed to update treatment: ${response.message}`);
                    }
                }
            }
    
            onClose();
        } catch (error) {
            console.error('An error occurred while saving:', error);
    
            const errorDetails = {
                error: error,
                parameters: JSON.stringify(localList),
                userName: currentUser.name,
                moduleName: 'ChangePriorityOfTemplatesPopup',
                functionName: 'handleSave',
            };
    
            await DBlogError(errorDetails);
        }
    };

    const DrawRow = (template, index) => {
        const isFirstItem = index === 0;
        const isLastItem = index === localList.length - 1;
    
        return (
            <Box
                key={template.TreatmentID}
                direction="row"
                align="center"
                justify="between"
                style={{ minHeight: "30px" }}
            >
                <Box width="150px" pad={{ vertical: "xsmall", horizontal: "xxsmall" }}>
                    <Text color="brand">{template.TreatmentName}</Text>
                </Box>
    
                <Box width="60px" pad={{ vertical: "xsmall", horizontal: "xxsmall" }}>
                    <TextInput
                        value={template.Priority ? template.Priority : ""}
                        style={{ padding: "3px 0 3px 10px" }}
                        readOnly
                    />
                </Box>
                <Box width="40px" pad={{ vertical: "xsmall", horizontal: "xxsmall" }} align="center" justify="around">
                    <Box
                        width="20px"
                        height="12px"
                        align="center"
                        justify="center"
                        background={isFirstItem ? "#c8dde6" : "#b8d0d9"} // Gray out the button if disabled
                        margin="2px"
                        onClick={!isFirstItem ? () => onMoveUp(template.TreatmentID) : null}
                        style={{ cursor: isFirstItem ? "not-allowed" : "pointer" }}
                    >
                        <FormUp color={isFirstItem ? "#76b9cf" : "brand"} />
                    </Box>
                    <Box
                        width="20px"
                        height="12px"
                        align="center"
                        justify="center"
                        background={isLastItem ? "#c8dde6" : "#b8d0d9"} // Gray out the button if disabled
                        margin="2px"
                        onClick={!isLastItem ? () => onMoveDown(template.TreatmentID) : null}
                        style={{ cursor: isLastItem ? "not-allowed" : "pointer" }}
                    >
                        <FormDown color={isLastItem ? "#76b9cf" : "brand"} />
                    </Box>
                </Box>
            </Box>
        );
    };
    
    const DrawTable = () => {
        if (!localList || localList.length === 0) {
            return null;
        }
        return localList.map((item, index) => DrawRow(item, index));
    };

    return (
        <Layer
            id="alertmsg"
            position="center"
            background="light-3"
        //    responsive={true}
            modal
        >
            <Box pad="small" margin="small" gap="xxsmall" width="medium" border>

                <Box style={{position:"relative"}}   margin={{bottom: 'xlarge'}}>
                    <Header margin="none" background="light-4" pad="small" height="xxsmall" justify="around">
                        <Text alignSelf="start" color="brand" size="large"  align="center" >
                            <strong>Template Priority Management</strong>
                        </Text>
                    </Header>
                    <Box  height="xsmall" width="xsmall" 
                        style={{position:"absolute", right: "40%", top: "60px"}}
                        right="0px"
                        >
                            <Image
                                size="cover"
                                src="Grape.png"                                  
                            />
                    </Box>  
                </Box>

                {error !== "" &&
                    <Box pad="small" >                          
                        <Text color="red" margin={{horizontal: "medium"}} size="medium">{error}</Text>
                    </Box>
                }
                <Box align="center" >                                    
                    <Text  margin="medium" textAlign="center" ><span style={{whiteSpace: 'pre-line'}}><strong><em>
                        Adjusting the priority of treatment templates. The first template will serve as the foundation for creating a new treatment, 
                        and the priority order will determine the display sequence in the dropdown list.</em></strong></span>
                    </Text>
                    {/* Middle part scrollable */}
                    <Box flex overflow='auto'>
                        {DrawTable()}
                    </Box>
                </Box>
                <Box
                    as="footer"
                    gap="small"
                    direction="row-responsive"
                    align="center"
                    justify="center"
                    pad={{ top: 'medium', bottom: 'xsmall' }}
                >
                    <Box direction="row" align="center" justify="around" width="80%">
                        <Button
                            label={
                                <Text >
                                    <strong>Save</strong>
                                </Text>
                            }
                            onClick={handleSave}
                            primary                  
                        /> 
                        <Button
                            style={{ color: `${theme.global.colors.brand}`, fontWeight: "bold"}}
                            label={
                                <Text color="brand">
                                    <strong>Cancel</strong>
                                </Text>
                            }
                             onClick={onClose}
                            secondary                  
                        />  
                    </Box>                    
                </Box>
            </Box>
        </Layer>
    )
}

// ------------------------  end of pop-up window for change priority  -------------------------------------



const TemplateTreatmentsPage = (props) => {
    const currentUser = useContext(UserContext);
    const { get, post } = useFetch(getDBURL());
    const { DBlogError } = useErrorLogger();

    const [valuesLoaded, setValuesLoaded] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [baseTreatment, setBaseTreatment] = useState("");
    const [treatmentComboList, setTreatmentComboList] = useState([]);
    const [treatmentValValues, setTreatmentValValues] = useState([]);
    const [treatmentValuesList, setTreatmentValuesList] = useState([]);

    const [pendingSelection, setPendingSelection] = useState(null); // a state variable to keep track of the treatment the user is trying to switch to.
    const [success, setSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [notificationMessage, setNotificationMessage] = useState("");
    const [hasEdit, setHasEdit] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);  // for ConfirmPopupWindow
    const [openChangePriority, setOpenChangePriority] = useState(false);  // for Change Priority of Templates Popup Window

    const getMaxTreatPriority = () => {
        const maxPriority = 0;
        if (Array.isArray(treatmentValuesList) && treatmentValuesList.length > 0) {
            return treatmentValuesList.reduce((max, element) => 
                Number(element.Priority) > max ? Number(element.Priority) : max, maxPriority
            ) + 1;
        }
        // console.log('maxPriority --->>>> --->>>   ', maxPriority);
        return maxPriority + 1;
    };


    const loadDefaultTreatments = async () => {
        try {
            const response = await get("/loadDefaultTreatments/dt=" + Date.now());
    
            if (!response.success) {
                throw new Error(`HTTP error! ${response.message}`);
            }

            const res = response.result;
            if (res && res.length > 0) {
                // console.log('res[0] >> ', res[0]);

                let localTreatmentComboList = [];
                let defaultTreatmentId = "";  // Initialize to null to hold the default treatment object
                let defaultTreatmentObject;
                let treatmentValuesMap = new Map();  // Initialize a map to group by TreatmentID
    
                res[0].forEach(item => {
                    // Prevent duplicate entries in localTreatmentComboList and skip IsDefault === 1 items
                    const exists = localTreatmentComboList.some(treatment => treatment.TreatmentID === item.DefTreatmentId);
                    if (!exists && item.IsDefault !== 1) {
                        localTreatmentComboList.push({
                            TreatmentID: item.DefTreatmentId,
                            TreatmentName: item.TreatmentName,
                            Priority: item.Priority
                        });
                    }

                    // Group items by TreatmentID
                    if (!treatmentValuesMap.has(item.DefTreatmentId)) {
                        treatmentValuesMap.set(item.DefTreatmentId, {
                            TreatmentID: item.DefTreatmentId,
                            TreatmentName: item.TreatmentName,
                            Priority: item.Priority,
                            CreateDate: item.CreateDate, 
                            CreatedBy: item.CreatedBy,                               
                            UpdatedAt: item.UpdatedAt,
                            UpdatedBy: item.UpdatedBy,
                            values: []
                        });
                    }
    
                    // Add values to the grouped TreatmentID
                    treatmentValuesMap.get(item.DefTreatmentId).values.push({
                        TreatmentValueID: item.Id,
                        LabelName: item.LabelName,
                        PhenologicalStageNum: item.PhenologicalStage,
                        Product: item.Product,
                        Dose: item.Dose,
                        OrderBy: item.OrderBy,
                        IsDefaultField: item.IsDefaultField,
                    });

                    // If the treatment is the default one, store its ID for later use
                    if (item.IsDefault === 1) {
                        defaultTreatmentId = item.DefTreatmentId;
                    }

                });
    
                // Set the baseTreatment state to the full default treatment object if it exists
                if (defaultTreatmentId !== "") {
                    defaultTreatmentObject = treatmentValuesMap.get(defaultTreatmentId);
                    setBaseTreatment(defaultTreatmentObject);
                    console.log('Template Treatment >> ', defaultTreatmentObject);
                }

                // Sort localTreatmentComboList by Priority
                localTreatmentComboList = localTreatmentComboList.sort((a, b) => a.Priority - b.Priority);
                // console.log('Treatment combo list >> ', localTreatmentComboList);

                // Set selected treatment
                if (!selectedTemplate) {
                    setSelectedTemplate(localTreatmentComboList.length > 0 ? treatmentValuesMap.get(localTreatmentComboList[0].TreatmentID) : defaultTreatmentObject);
                    setTreatmentValValues(localTreatmentComboList.length > 0 ? treatmentValuesMap.get(localTreatmentComboList[0].TreatmentID).values : defaultTreatmentObject.values);    
                }
                
                // Convert the map to an array and update the state
                setTreatmentValuesList(Array.from(treatmentValuesMap.values()));
    
                // Store the treatment combo list in the state
                setTreatmentComboList(localTreatmentComboList);
                setValuesLoaded(true);
               
            } else {
                console.warn('No data received for default treatments.');
            }
    
        } catch (error) {
            console.error('Error in function loadDefaultTreatments', error.message);
    
            // Construct error object
            const errorDetails = {
                error: error,
                parameters: { docType: 'defaultTreatment' },
                userName: currentUser.name,
                moduleName: 'TemplateTreatmentsPage',
                functionName: 'loadDefaultTreatments',
            };
            // Log the error
            await DBlogError(errorDetails);
        }
    };

    useEffect(() => { 
        const load = async () => {
            await loadDefaultTreatments();
        }
        load();
    }, []); 

    // hides the "Warning" popup window 
    const onWarnClose = () => {
        setWarningMessage('');
        setShowWarning(false);
    }; 

    const onConfirmClose = () => {
        setOpenConfirm(false);
        setHasEdit(false);
    };
    
    const onConfirm = async () => {
        // Saving the Previous Treatment (template)
        await saveDefaultTreatment();  // Save the edited data
        onConfirmClose();  // Close the confirmation modal
        handleSelectionChange(pendingSelection);  // Switch to the selected treatment
        setPendingSelection(null);  // Clear the pending selection
    };
    
    const onCancel = () => {
        onConfirmClose();  // Close the confirmation modal
        handleSelectionChange(pendingSelection);  // Switch to the selected treatment without saving
        setPendingSelection(null);  // Clear the pending selection
    };

    const onChangeSelected = (treatment) => {
        if (treatment && selectedTemplate.TreatmentID === treatment.TreatmentID) return;
    
        if (!treatment || !treatmentValuesList || treatmentValuesList.length === 0) {
            setSelectedTemplate(baseTreatment);
            setTreatmentValValues(baseTreatment.values);
            return;
        }
    
        if (hasEdit) {
            setPendingSelection(treatment);  // Store the treatment the user wants to switch to
            setOpenConfirm(true);  // Show the confirmation modal
            return;
        }
    
        handleSelectionChange(treatment);
    };
    
    // Helper Function handleSelectionChange:
    // This function is responsible for switching the selected treatment and updating the related state.
    const handleSelectionChange = (treatment) => {
        const newState = treatmentValuesList.find((tr) => tr.TreatmentID === treatment.TreatmentID);
    
        if (newState) {
            setSelectedTemplate(newState);
            setTreatmentValValues(newState.values);
        } else {
            console.warn("Selected treatment not found, falling back to template treatment.");
            setSelectedTemplate(baseTreatment);
            setTreatmentValValues(baseTreatment.values);
        }
    };

    async function saveDefaultTreatment() {
        try {
            if (selectedTemplate.TreatmentName === baseTreatment.TreatmentName || selectedTemplate.TreatmentName === "") {
                setWarningMessage(`Treatment without name can't be added.`);
                setShowWarning(true);
                return;
            }
    
            // Check for duplicate treatment names
            if (treatmentValuesList.some(tv => 
                tv.TreatmentName.toLowerCase() === selectedTemplate.TreatmentName.toLowerCase() &&
                selectedTemplate.TreatmentID === baseTreatment.TreatmentID)) {
                setWarningMessage(`Treatment value with name "${selectedTemplate.TreatmentName}" already exists.`);
                setShowWarning(true);
                return;
            }
    
            // Determine whether this is a new template or an update to an existing one
            const isNewTemplate = selectedTemplate.TreatmentID === baseTreatment.TreatmentID;
            const template = {
                TreatmentID: isNewTemplate ? generateSyncId() : selectedTemplate.TreatmentID,  
                TreatmentName: selectedTemplate.TreatmentName,     
                IsDefault: 0,
                Priority: isNewTemplate ? getMaxTreatPriority() : selectedTemplate.Priority,
                CreateDate: isNewTemplate ? correctTimeZone(new Date()) : selectedTemplate.CreateDate,
                CreatedBy: isNewTemplate ? currentUser.name : selectedTemplate.CreatedBy,         
                UpdatedAt: isNewTemplate ? null : correctTimeZone(new Date()),
                UpdatedBy: isNewTemplate ? null : currentUser.name,
                LastSyncDate: correctTimeZone(new Date()),                 
            };

            const response = await post("/updateDefaultTreatment", template);
    
            if (!response.success) {
                throw new Error(`HTTP error! ${response.message}`);
            }

            const updatedValues = treatmentValValues.map(el => ({
                ...el,
                TreatmentID: template.TreatmentID,
                TreatmentValueID: isNewTemplate ? generateSyncId() : el.TreatmentValueID, 
            }));
    
            // Update the state with the new treatment values
            setTreatmentValValues(updatedValues);            
    
            // Call saveTreatmentValues immediately after setting treatment values
            await saveTreatmentValues(updatedValues);
            setHasEdit(false);

            // Reload the default treatments with the updated state
            await loadDefaultTreatments();

    
            return true;
        } catch (error) {
            console.error('Error in saveDefaultTreatment function: ', error.message);
            setShowError(true);
            setNotificationMessage(error.message);
    
            // Log the error
            const errorDetails = {
                error: error,
                parameters: {}, 
                userName: currentUser.name,
                moduleName: 'TemplateTreatmentsPage',
                functionName: 'saveDefaultTreatment',
            };
            await DBlogError(errorDetails);
    
            return false;
        }         
    };
    

    // saving treatmentTable values
    // updateTreatmentValue inserts into table 
    // or updates table VEM_DefaultTreatmentValues
    async function saveTreatmentValues(values) {
        try {
            const response = await post("/updateDefaultTreatmentValue", values);

            // Check if the response is successful
            if (!response.success) {
                throw new Error(`HTTP error! ${response.message}`);
            } 

            const res = response.result;
            // console.log('In saveTreatmentValues res >> ', res);
            if (res !== undefined) {
                setValuesLoaded(true);
                setSuccess(true);
                setNotificationMessage("Treatment updated successfully.");
                setHasEdit(false);                    
                return true;
            }
        }
        catch (error) {
            console.error('Error In saveTreatmentValues f-n: ', error.message);
            setShowError(true);
            setNotificationMessage('ERROR SAVING TREATMENT.'); 

            // Construct error object
            const errorDetails = {
                error: error,
                parameters: JSON.stringify(treatmentValValues),
                userName: currentUser.name,
                moduleName: 'TemplateTreatmentsPage',
                functionName: 'saveTreatmentValues',
            }
            // Log the error
            await DBlogError(errorDetails);            

            return false;
        }
    };     


    return (
        <ResponsiveContext.Consumer>
        {screenSize => (         
            <Box fill>
                <Box
                    style={{ 
                        position: 'fixed',
                        top: 0,
                        zIndex: 10,
                        height: calculateHeight(3)
                    }}>                
                    <HomePageNav 
                        isDefault={true}
                        hasEdit={hasEdit} 
                        save={saveDefaultTreatment}
                    />
                </Box>

                <Box>
                    <Box width="98%" margin="0 auto" style={{ height:"40px", minHeight:"40px"}}>
                        <Box style={{position:"relative"}}>
                            <Box background="neutral-3" pad={{vertical: "small"}} margin={{vertical: "xxsmall"}} >
                                <Box direction="row" justify="center" align="center">
                                    <Text size="large" weight="bold" color="#ffffff" > Treatment Templates </Text>
                                </Box>              
                            </Box>  
                            <Box height="xsmall" width="xsmall"  
                                style={{position:"absolute", right: "3%", top: "15px"}}
                                right="0px"
                                >
                                    <Image
                                        size="contain"
                                        src="Grape.png" 
                                        width={screenSize === "small" ? "70%" : screenSize === "medium" ? "100%" : "150%"}                                     
                                        height={screenSize === "small" ? "70%" : screenSize === "medium" ? "100%" : "150%"}                                    
                                    />
                            </Box>                         
                        </Box>      

                    </Box>

                    <Box  alignSelf="center">

                        {!valuesLoaded && (
                        <Box align="center" direction="row" gap="small" pad="small">
                            <Spinner  size="medium"/> <Text>Loading...</Text>
                        </Box>
                        )}

                        {showWarning && (
                            <WarningPopupWindow msg={warningMessage} close={onWarnClose} />
                        )}
                        {showError && (
                            <Notification
                                status="critical"
                                message={notificationMessage} // "ERROR SAVING TREATMENT." 
                                onClose={() => setShowError(false)}
                            />                        )}
                        {success && (
                            <Notification
                                status="normal"
                                message={notificationMessage} // {`NEW TEMPLATE TREATMENT SAVED SUCCESSFULLY.`}
                                onClose={() => setSuccess(false)}
                            />
                        )} 

                        {openConfirm && (
                            <ConfirmPopupWindow
                            msg={"Unsaved changes. Do you want to save?"}
                            onClose={onCancel}
                            onConfirm={onConfirm}
                            ></ConfirmPopupWindow>
                        )}     

                        {valuesLoaded &&
                        <Box direction="row-responsive" gap="small" pad="small">
                            <Button 
                                style={{width: "170px"}} 
                                label="Save" 
                                onClick={saveDefaultTreatment}
                                disabled={!hasEdit}  
                                primary /> 
                            <Button 
                                style={{width: "170px", background: "green", borderColor: "green"}} 
                                label="Add new Template"
                                // label="Add new" 
                                onClick={() => onChangeSelected(baseTreatment)}  
                                disabled={selectedTemplate.TreatmentID === baseTreatment.TreatmentID}
                                primary />     
                            
                            <Button 
                                style={{width: "170px", background: "#346d82", borderColor: "#346d82"}} 
                                label="ChangeTemplate Order" 
                                onClick={() => setOpenChangePriority(true)}  
                                primary /> 
                                                                                 
                        </Box>
                        } 

                        {valuesLoaded && 
                        <>
                        <TreatmentTable
                            tableWdth={screenSize === 'small' ? "100%" : "825px"} 
                            tableHeight= "84%"
                            treatmentValues={treatmentValValues}
                            setTreatmentValues={setTreatmentValValues}
                            stateHandler={setSelectedTemplate}
                            isDefault={true}
                            treatmentProperties={selectedTemplate}
                            baseTreatmentTemplate={baseTreatment}
                            setHasEdit={setHasEdit}
                            templatesComboList={treatmentComboList}
                            selectedTemplate={selectedTemplate}
                            onChangeSelected={onChangeSelected}
                        />
                        {openChangePriority && 
                        <ChangePriorityOfTemplatesPopup 
                            onClose={() => {
                                setOpenChangePriority(false);
                                loadDefaultTreatments()}} 
                            templatesList={treatmentValuesList.filter(item => item.TreatmentID !== baseTreatment.TreatmentID)}
                            baseTreatment={baseTreatment} // Pass the base treatment separately
                            setHasEdit={setHasEdit}
                            updateTreatmentValuesList={setTreatmentValuesList}
                        />
                        } 
                        </>
                        }
                    </Box>
                </Box>
            </Box>
        )}
        </ResponsiveContext.Consumer>

    );
    
};

export default TemplateTreatmentsPage;
